import { useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import styled from "styled-components"
import type { UITypes } from "types"

import {
  useLazyGetIntelligenceReportingPeriodQuery,
  useLazyGetIntelligenceReportsQuery,
} from "store/api/contractorsApi"
import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { parseDateField } from "utils/dateUtils"
import { useUserProfile } from "hooks/useUserProfile"

import { EmptyState } from "../FullIntelligenceReport/EmptyState"
import { Toolbar } from "../FullIntelligenceReport/Toolbar"

const Wrapper = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  padding-bottom: 24px;

  @media (max-width: 768px) {
    padding-bottom: 12px;
  }
`

const Body = styled.div`
  padding: 24px 0 0;

  @media (max-width: 768px) {
    padding: 12px 0 0;
  }
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 80px 24px;
  color: #475569;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`

type Status = "loading" | "not-found" | "no-access" | "error"

interface Period {
  start_ts_utc: string
  end_ts_utc: string
}

function buildNotFoundDescription(deviceSN: string, period: Period | null): string {
  if (period) {
    const periodLabel = `${parseDateField(period.start_ts_utc)} – ${parseDateField(period.end_ts_utc)}`
    return `No report found for device ${deviceSN} for the period ${periodLabel}.`
  }
  return `No report found for device ${deviceSN}.`
}

const NO_ACCESS_DESCRIPTION =
  "Your account doesn't have permission to view reports for this device. Reach out to the device owner."

const noop = () => {}

export function ReportRedirectBySn() {
  const { deviceSN } = useParams<{ deviceSN: string }>()
  const navigate = useNavigate()
  const { userProfile } = useUserProfile()
  const hasEPlusPlan = userProfile?.contractor?.subscription_plan === SubscriptionPlan.E_PLUS
  const [fetchDevices] = useLazyGetDevicesQuery()
  const [fetchPeriod] = useLazyGetIntelligenceReportingPeriodQuery()
  const [fetchReports] = useLazyGetIntelligenceReportsQuery()
  const [status, setStatus] = useState<Status>("loading")
  const [period, setPeriod] = useState<Period | null>(null)

  useEffect(() => {
    if (!deviceSN) {
      setStatus("not-found")
      return
    }
    let cancelled = false

    const run = async () => {
      try {
        const devices = await fetchDevices({ sn: deviceSN } as UITypes.Params).unwrap()
        if (cancelled) return
        if (!devices.data.length) {
          setStatus("no-access")
          return
        }
      } catch {
        if (!cancelled) setStatus("no-access")
        return
      }

      try {
        const periodResponse = await fetchPeriod().unwrap()
        if (cancelled) return
        setPeriod(periodResponse.data)

        const reports = await fetchReports({
          sn: deviceSN,
          start_ts_utc: periodResponse.data.start_ts_utc,
          end_ts_utc: periodResponse.data.end_ts_utc,
        } as UITypes.Params).unwrap()
        if (cancelled) return

        const match = reports.data.find(r => r.sn === deviceSN) ?? reports.data[0]
        if (!match) {
          setStatus("not-found")
          return
        }
        navigate(`/intelligence-reports/${match.report_id}/full`, { replace: true })
      } catch {
        if (!cancelled) setStatus("error")
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [deviceSN, fetchDevices, fetchPeriod, fetchReports, navigate])

  if (userProfile && !hasEPlusPlan) {
    return <Navigate to="/intelligence-reports" replace />
  }

  const renderBody = () => {
    if (status === "loading") return <LoadingMessage>Loading report...</LoadingMessage>
    if (status === "no-access") return <EmptyState title="Access denied" description={NO_ACCESS_DESCRIPTION} />
    if (status === "error") return <EmptyState title="Failed to load report" description="Please try again later." />
    if (!deviceSN) return <EmptyState title="No report found" description="Missing device serial number." />
    return <EmptyState title="No report found" description={buildNotFoundDescription(deviceSN, period)} />
  }

  return (
    <Wrapper>
      <Toolbar
        title="Intelligence Report"
        subtitle={deviceSN ? `SN: ${deviceSN}` : undefined}
        showActions={false}
        onDownloadPdf={noop}
        onCopyLink={noop}
        isDownloading={false}
        isDownloadDisabled
      />
      <Body>{renderBody()}</Body>
    </Wrapper>
  )
}
